/* main.js — demo harness for the Pixi shrine scene.
 *
 * Drives the controller the same way the v1 game logic does:
 *   tap the ground -> walk toward it; camera clamps to 260..640 and follows;
 *   the hamster wanders on a slow sine path; markers, ripples, dim/writing.
 */

import { createShrineScene, WALK_MIN, WALK_MAX, CX } from './scene.js';

const canvas = document.getElementById('scene');
const statsEl = document.getElementById('stats');

const qs = new URLSearchParams(location.search);

const scene = await createShrineScene(canvas, {
  me: 'D',
  other: 'Y',
  cacheStatic: qs.get('cache') !== '0',
  autoStart: false,          // we drive the loop so we can measure it
  antialias: qs.get('aa') === '1',
});

window.__scene = scene;      // handy for console poking / automated probes

/* ============================ walk state ============================ */

const WALK_SPEED = 170;      // px/s, straight from the original

/* ?freeze=1&me=585&ot=660&cam=300&nohud=1 pins the scene for screenshot diffs
   against the original SVG. Demo-only; the scene module knows nothing of it. */
const FREEZE = qs.get('freeze') === '1';
const FIX_CAM = qs.has('cam') ? +qs.get('cam') : null;

const me = { x: +(qs.get('me') || 585), target: FREEZE ? null : 460, dir: -1, walking: false };
const her = { x: +(qs.get('ot') || 660), dir: -1, t: 0 };

if (qs.get('nohud') === '1') {
  for (const id of ['title', 'stats', 'hint', 'hudBar']) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
}

let markers = true;
let dim = false;
let writing = false;
let omikuji = false;
let rainOn = true;

scene.setAvatar('me', { x: me.x, dir: me.dir, visible: true });
scene.setAvatar('other', { x: her.x, dir: her.dir, visible: true });

/* ============================ input ============================ */

function onTap(clientX, clientY) {
  const p = scene.toWorld(clientX, clientY);
  if (p.y < 540) return;                    // only the ground half is walkable
  const tx = Math.max(WALK_MIN, Math.min(WALK_MAX, p.x));
  me.target = tx;
  scene.ripple(tx);
}

canvas.addEventListener('pointerdown', (e) => {
  onTap(e.clientX, e.clientY);
});

/* ============================ buttons ============================ */

function wire(id, get, set) {
  const el = document.getElementById(id);
  el.addEventListener('click', () => {
    set(!get());
    el.classList.toggle('on', get());
  });
  el.classList.toggle('on', get());
}

wire('bDim', () => dim, (v) => { dim = v; scene.setDim(v); });
wire('bWrite', () => writing, (v) => {
  writing = v;
  scene.setAvatar('me', { writing: v });
  scene.setAvatar('other', { writing: v });
});
wire('bMarks', () => markers, (v) => { markers = v; scene.setMarkersVisible(v); });
wire('bOmi', () => omikuji, (v) => {
  omikuji = v;
  scene.setOmikujiVisible(v);
  scene.setStickOut(v);
  // the omikuji state also dims the bell + saisen box in the original
  scene.setDim(v || dim);
});
wire('bRain', () => rainOn, (v) => { rainOn = v; scene.setRainVisible(v); });

/* ============================ loop ============================ */

let last = 0;
let fpsAcc = 0, fpsCount = 0, fps = 0, worstFrame = 0;
const samples = [];

function loop(now) {
  if (scene.destroyed) return;
  requestAnimationFrame(loop);
  const dtRaw = last ? (now - last) / 1000 : 0.016;
  last = now;
  const dt = Math.min(0.05, dtRaw);

  /* --- me: walk toward the tapped target --- */
  me.walking = false;
  if (me.target != null) {
    const dx = me.target - me.x;
    if (Math.abs(dx) > 3) {
      me.walking = true;
      me.dir = dx > 0 ? 1 : -1;
      const sp = WALK_SPEED * dt;
      me.x += Math.max(-sp, Math.min(sp, dx));
    } else {
      me.x = me.target;
      me.target = null;
    }
  }
  scene.setAvatar('me', { x: me.x, dir: me.dir, walking: me.walking });

  /* --- her: a slow sine wander between the rack and the hut --- */
  if (!FREEZE) {
    her.t += dt;
    const hx = 350 + Math.sin(her.t * 0.28) * 290;
    const moving = Math.abs(hx - her.x) > 0.6;
    if (moving) her.dir = hx > her.x ? 1 : -1;
    her.x = hx;
    scene.setAvatar('other', { x: her.x, dir: her.dir, walking: moving });
  }

  /* --- camera follow, clamped exactly like the original --- */
  scene.setCamera(
    FIX_CAM != null ? FIX_CAM : (markers ? Math.max(260, Math.min(640, me.x)) : CX),
    FIX_CAM != null);

  scene.tick(dt);
  scene.render();

  /* --- fps --- */
  fpsAcc += dtRaw; fpsCount++;
  if (dtRaw > worstFrame && fpsCount > 5) worstFrame = dtRaw;
  if (fpsAcc >= 0.5) {
    fps = fpsCount / fpsAcc;
    samples.push(fps);
    if (samples.length > 40) samples.shift();
    fpsAcc = 0; fpsCount = 0;
    const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
    statsEl.textContent =
      `${fps.toFixed(1)} fps\n` +
      `avg ${avg.toFixed(1)}\n` +
      `worst ${(1000 * worstFrame).toFixed(1)} ms\n` +
      `x ${me.x.toFixed(0)}  cam ${scene.camera.toFixed(0)}\n` +
      `scale ${scene.scale.toFixed(3)}  dpr ${(window.devicePixelRatio || 1).toFixed(1)}`;
    worstFrame = 0;
  }
}

requestAnimationFrame(loop);

/* ============================ resize ============================ */

let resizeT = 0;
function onResize() {
  clearTimeout(resizeT);
  resizeT = setTimeout(() => scene.resize(), 60);
}
window.addEventListener('resize', onResize);
window.addEventListener('orientationchange', onResize);
if (window.visualViewport) window.visualViewport.addEventListener('resize', onResize);

/* ---- a 5s rAF probe any automated check can read ---- */
window.__measureFps = function (ms = 5000) {
  return new Promise((resolve) => {
    let n = 0; const t0 = performance.now();
    const frames = [];
    (function step(t) {
      frames.push(t);
      n++;
      if (t - t0 < ms) requestAnimationFrame(step);
      else {
        const dur = (frames[frames.length - 1] - frames[0]) / 1000;
        const deltas = [];
        for (let i = 1; i < frames.length; i++) deltas.push(frames[i] - frames[i - 1]);
        deltas.sort((a, b) => a - b);
        resolve({
          frames: n,
          seconds: +dur.toFixed(2),
          fps: +((n - 1) / dur).toFixed(2),
          medianMs: +deltas[deltas.length >> 1].toFixed(2),
          p95Ms: +deltas[Math.floor(deltas.length * 0.95)].toFixed(2),
          maxMs: +deltas[deltas.length - 1].toFixed(2),
        });
      }
    })(performance.now());
  });
};
